import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import humanTime from 'flarum/common/utils/humanTime';
const PAGE = 24;

/** Brightness 0..1 of a #rrggbb colour */
const lightness = hex => {
  const m6 = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim());
  if (!m6) return 0;
  const n = parseInt(m6[1], 16);
  return (0.299 * (n >> 16 & 255) + 0.587 * (n >> 8 & 255) + 0.114 * (n & 255)) / 255;
};

/** Topic colour as text: light colours (e.g. a yellow tag) are darkened so they stay readable */
const readable = hex => {
  if (lightness(hex) < 0.62) return hex || undefined;
  const n = parseInt(String(hex).replace('#', ''), 16);
  const dark = c => Math.round(c * 0.45);
  return 'rgb(' + dark(n >> 16 & 255) + ', ' + dark(n >> 8 & 255) + ', ' + dark(n & 255) + ')';
};
const LEAD_DAYS = 7; // a marked lead older than this gives way to the newest story with an image

/**
 * /news (Blind-style): the lead story in the centre, three text stories on the left, Most popular on
 * the right, then image cards. The lead is marked by moderators ("Make lead story" in a news
 * discussion's menu); without one, the newest story with an image leads.
 */
export default class NewsPage extends Page {
  oninit(vnode) {
    super.oninit(vnode);
    this.items = [];
    this.popular = [];
    this.marked = null; // the marked lead, if loaded separately
    this.loading = true;
    this.more = false;
    this.tag = null;
    this.tabs = null;
    app.setTitle('News');
    app.setTitleCount(0);
    this.load(true);
  }
  markedLeadId() {
    const id = parseInt(app.forum.attribute('vouchaNewsLead'), 10);
    const at = Date.parse(app.forum.attribute('vouchaNewsLeadAt') || '');
    return id && (!at || Date.now() - at < LEAD_DAYS * 864e5) ? String(id) : null;
  }
  load(reset) {
    if (reset) {
      this.items = [];
      this.popular = [];
    }
    this.loading = true;
    const filter = {
      news: 1
    };
    if (this.tag) filter.tag = this.tag;
    const lists = [app.store.find('discussions', {
      filter,
      sort: '-createdAt',
      include: 'tags,user',
      page: {
        offset: this.items.length,
        limit: PAGE
      }
    })];
    if (reset) {
      lists.push(app.store.find('discussions', {
        filter,
        sort: '-vouchaPopular',
        include: 'tags,user',
        page: {
          limit: 5
        }
      }).catch(() => []));
      const leadId = !this.tag && this.markedLeadId();
      if (leadId) lists.push(app.store.find('discussions', leadId, {
        include: 'tags,user'
      }).catch(() => null));
    }
    return Promise.all(lists).then(_ref => {
      let [list, popular, marked] = _ref;
      const found = Array.isArray(list) ? list : [];
      this.items = this.items.concat(found);
      this.more = found.length >= PAGE;
      if (reset) {
        this.popular = Array.isArray(popular) ? popular : [];
        this.marked = marked && marked.attribute && marked.attribute('vouchaNews') ? marked : null;
      }
      if (!this.tabs && !this.tag) this.tabs = this.topics(this.items);
    }).catch(() => this.more = false).finally(() => {
      this.loading = false;
      m.redraw();
    });
  }
  topic(d) {
    const tags = d.tags && d.tags() || [];
    return tags.find(t => t && !t.parent()) || tags[0] || null;
  }
  topics(list) {
    const count = {};
    const tagOf = {};
    list.forEach(d => {
      const t = this.topic(d);
      if (t) {
        count[t.slug()] = (count[t.slug()] || 0) + 1;
        tagOf[t.slug()] = t;
      }
    });
    return Object.keys(count).sort((a, b) => count[b] - count[a]).slice(0, 6).map(slug => ({
      slug,
      name: tagOf[slug].name()
    }));
  }
  choose(slug) {
    if (this.tag === slug) return;
    this.tag = slug;
    this.load(true);
  }

  /** The lead: the marked story (All tab), otherwise the newest story with an image */
  lead() {
    if (!this.tag && this.marked) return this.marked;
    return this.items.find(d => d.attribute('vouchaNewsImage')) || this.items[0] || null;
  }
  link(d, children, className) {
    const url = app.route.discussion(d);
    return m("a", {
      className: className,
      href: url,
      onclick: e => {
        if (!e.ctrlKey && !e.metaKey) {
          e.preventDefault();
          m.route.set(url);
        }
      }
    }, children);
  }
  image(d, extra) {
    if (extra === void 0) {
      extra = '';
    }
    const t = this.topic(d);
    const img = d.attribute('vouchaNewsImage');
    return m("div", {
      className: 'VouchaNews-image' + extra + (img ? '' : ' is-placeholder') + (!img && t && lightness(t.color()) >= 0.62 ? ' is-light' : ''),
      style: img ? {
        backgroundImage: 'url("' + img + '")'
      } : {
        background: t && t.color() || '#1f3d35'
      }
    }, img ? null : m("i", {
      className: t && t.icon() || 'fas fa-newspaper',
      "aria-hidden": "true"
    }));
  }
  topicLabel(d) {
    const t = this.topic(d);
    return t ? m("span", {
      className: "VouchaNews-topic",
      style: {
        color: readable(t.color())
      }
    }, t.name()) : null;
  }
  meta(d) {
    const author = d.user && d.user();
    const source = author ? author.displayName() : d.attribute('vouchaNewsSource');
    const replies = Math.max(0, (d.commentCount() || 1) - 1);
    return m("div", {
      className: "VouchaNews-meta"
    }, source ? m("span", {
      className: "VouchaNews-source"
    }, source) : null, m("span", null, humanTime(d.createdAt())), replies ? m("span", null, m("i", {
      className: "far fa-comment",
      "aria-hidden": "true"
    }), " ", replies) : null);
  }
  card(d) {
    return this.link(d, [this.image(d), m("div", {
      className: "VouchaNews-body"
    }, this.topicLabel(d), m("h3", {
      className: "VouchaNews-title"
    }, d.title()), this.meta(d))], 'VouchaNews-card');
  }
  view() {
    const lead = this.lead();
    const rest = this.items.filter(d => !lead || d.id() !== lead.id());
    const side = rest.slice(0, 3);
    const grid = rest.slice(3);
    return m("div", {
      className: "VouchaNews container"
    }, m("header", {
      className: "VouchaNews-head"
    }, m("h1", null, "News"), app.forum.attribute('vouchaGoogleFollowUrl') ? m("a", {
      className: "VouchaNews-google",
      href: app.forum.attribute('vouchaGoogleFollowUrl'),
      target: "_blank",
      rel: "noopener",
      title: "Add Voucha as a preferred source on Google"
    }, m("svg", {
      width: "16",
      height: "16",
      viewBox: "0 0 48 48",
      "aria-hidden": "true"
    }, m("path", {
      fill: "#EA4335",
      d: "M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.6 5.4 2.6 13.3l7.9 6.1C12.4 13.6 17.7 9.5 24 9.5z"
    }), m("path", {
      fill: "#4285F4",
      d: "M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.7 6c4.5-4.2 6.9-10.3 6.9-17.7z"
    }), m("path", {
      fill: "#FBBC05",
      d: "M10.5 28.6c-.5-1.4-.8-3-.8-4.6s.3-3.2.8-4.6l-7.9-6.1C1 16.6 0 20.2 0 24s1 7.4 2.6 10.7l7.9-6.1z"
    }), m("path", {
      fill: "#34A853",
      d: "M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.7-6c-2.1 1.4-4.9 2.3-8.2 2.3-6.3 0-11.6-4.1-13.5-9.9l-7.9 6.1C6.6 42.6 14.6 48 24 48z"
    })), "Follow us on Google") : null, app.forum.attribute('vouchaWhatsappChannelUrl') ? m("a", {
      className: "VouchaNews-google VouchaNews-whatsapp",
      href: app.forum.attribute('vouchaWhatsappChannelUrl'),
      target: "_blank",
      rel: "noopener",
      title: "Opens our channel in WhatsApp, where you tap Follow"
    }, m("i", {
      className: "fab fa-whatsapp",
      "aria-hidden": "true"
    }), "Follow on WhatsApp") : null, this.tabs && this.tabs.length > 1 ? m("nav", {
      className: "VouchaNews-tabs",
      "aria-label": "Topics"
    }, m("button", {
      type: "button",
      className: !this.tag ? 'is-on' : '',
      onclick: () => this.choose(null)
    }, "All"), this.tabs.map(tab => m("button", {
      type: "button",
      className: this.tag === tab.slug ? 'is-on' : '',
      onclick: () => this.choose(tab.slug)
    }, tab.name))) : null), lead ? m("section", {
      className: "VouchaNews-top"
    }, this.link(lead, [this.image(lead, ' VouchaNews-image--lead'), m("div", {
      className: "VouchaNews-leadBody"
    }, this.topicLabel(lead), m("h2", {
      className: "VouchaNews-leadTitle"
    }, lead.title()), lead.attribute('vouchaNewsExcerpt') ? m("p", {
      className: "VouchaNews-excerpt"
    }, lead.attribute('vouchaNewsExcerpt')) : null, this.meta(lead))], 'VouchaNews-lead'), m("div", {
      className: "VouchaNews-side"
    }, side.map(d => this.link(d, [this.topicLabel(d), m("h3", {
      className: "VouchaNews-sideTitle"
    }, d.title()), d.attribute('vouchaNewsExcerpt') ? m("p", {
      className: "VouchaNews-sideExcerpt"
    }, d.attribute('vouchaNewsExcerpt')) : null, this.meta(d)], 'VouchaNews-sideItem'))), this.popular.length ? m("aside", {
      className: "VouchaNews-popular",
      "aria-label": "Most popular"
    }, m("h2", null, "Most popular"), m("ol", null, this.popular.map((d, i) => m("li", null, this.link(d, [m("span", {
      className: "VouchaNews-rank"
    }, i + 1), m("span", {
      className: "VouchaNews-popularText"
    }, this.topicLabel(d), m("strong", null, d.title())), this.image(d, ' VouchaNews-thumb')], 'VouchaNews-popularItem'))))) : null) : null, grid.length ? m("div", {
      className: "VouchaNews-grid"
    }, grid.map(d => this.card(d))) : null, this.loading ? m(LoadingIndicator, null) : null, !this.loading && !this.items.length ? m("p", {
      className: "VouchaNews-empty"
    }, "No news yet.") : null, !this.loading && this.more ? m("div", {
      className: "VouchaNews-more"
    }, m("button", {
      type: "button",
      className: "Button",
      onclick: () => this.load(false)
    }, "Load more")) : null);
  }
}